/**
 * Admin-permission predicates — the portal's HALF of the roles → permissions
 * model. `permissions` on /api/me is resolved server-side (roles + module
 * entitlement) by the same code the route guards use; the portal only ever
 * asks "is the permission in the list". Roles gate nav items exactly like
 * scopes gate agent tools.
 */
import type { MeResponse } from './types.js';

type MeUser = MeResponse['user'];

/** Admin permission keys, mirroring `PERMS` in sc-platform's roles/permissions.ts. */
export const PERMS = {
  usage: { view: 'usage.view' },
  events: { view: 'events.view' },
  feedback: { view: 'feedback.view' },
  integrations: { manage: 'integrations.manage' },
  webhooks: { manage: 'webhooks.manage' },
  workflows: { manage: 'workflows.manage' },
  users: { manage: 'users.manage' },
  apiKeys: { manage: 'apikeys.manage' },
  notifications: { manage: 'notifications.manage' },
  assistant: { manage: 'assistant.manage' },
  bots: { manage: 'bots.manage' },
  knowledge: { view: 'knowledge.view', manage: 'knowledge.manage' },
} as const;

/** Any admin permission key; a typo fails to compile. */
export type Permission = {
  [Area in keyof typeof PERMS]: (typeof PERMS)[Area][keyof (typeof PERMS)[Area]];
}[keyof typeof PERMS];

export const hasPermission = (user: MeUser, permission: Permission): boolean =>
  (user.permissions ?? []).includes(permission);

export const hasAnyPermission = (user: MeUser, permissions: readonly Permission[]): boolean =>
  permissions.some((p) => hasPermission(user, p));

/** Reports is a user feature: the Reports scopes open its pages, not an admin role. */
export const REPORT_SCOPES = ['Reports.Read', 'Reports.Write'] as const;
export const canOpenReports = (user: MeUser): boolean =>
  REPORT_SCOPES.some((scope) => (user.effectiveScopes ?? []).includes(scope));

/** Building, sharing and scheduling reports: Reports.Write. */
export const canWriteReports = (user: MeUser): boolean =>
  (user.effectiveScopes ?? []).includes('Reports.Write');

/** May enter the admin shell at all — any admin capability, or Reports, shows the door. */
export const hasAdminAccess = (user: MeUser): boolean =>
  (user.permissions ?? []).length > 0 || canOpenReports(user);

/** Holds the FULL feedback queue (vs. the knowledge_base-pinned slice). */
export const canTriageFeedback = (user: MeUser): boolean =>
  hasPermission(user, PERMS.feedback.view);

/**
 * Where the Admin link lands: the first surface this user may see, in nav
 * order — an admin gets Usage as before; a KNOWLEDGE_ADMIN lands on
 * Categories rather than a redirect back to chat.
 */
const holds = (permission: Permission) => (user: MeUser) => hasPermission(user, permission);
const LANDING_ORDER: ReadonlyArray<readonly [(user: MeUser) => boolean, string]> = [
  [holds(PERMS.usage.view), '/usage'],
  [canOpenReports, '/reports'],
  [holds(PERMS.events.view), '/events'],
  [holds(PERMS.feedback.view), '/feedback'],
  [holds(PERMS.integrations.manage), '/connections'],
  [holds(PERMS.knowledge.view), '/knowledge/categories'],
  [holds(PERMS.workflows.manage), '/settings/workflows'],
  [holds(PERMS.users.manage), '/users'],
];

export const adminLandingPath = (user: MeUser): string =>
  LANDING_ORDER.find(([sees]) => sees(user))?.[1] ?? '/';
